import React from 'react';
import { Drawer, Empty, Spin, Table, Tag } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { GET_LOADING_POLICY_LOGS, LoadingPolicyLog } from '../graphql/loadingPolicyGraphql';
import { diffSnapshot } from '../loadingPolicyForm';

type LoadingPolicyLogDrawerProps = {
  policyId: string | null;
  onClose: () => void;
};

const ACTION_TAGS: Record<LoadingPolicyLog['action'], { color: string; label: string }> = {
  CREATE: { color: 'green', label: '新增' },
  UPDATE: { color: 'blue', label: '修改' },
  DELETE: { color: 'red', label: '删除' },
};

const pretty = (json: string | null): string => {
  if (!json) return '—';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

const LoadingPolicyLogDrawer: React.FC<LoadingPolicyLogDrawerProps> = ({ policyId, onClose }) => {
  const { data, loading } = useQuery<{ getLoadingPolicyLogs: LoadingPolicyLog[] }>(GET_LOADING_POLICY_LOGS, {
    variables: { policyId },
    skip: !policyId,
    fetchPolicy: 'network-only',
  });
  const logs = data?.getLoadingPolicyLogs ?? [];

  const columns: TableColumnsType<LoadingPolicyLog> = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: number | null) => (v ? dayjs.unix(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '动作',
      dataIndex: 'action',
      width: 80,
      render: (v: LoadingPolicyLog['action']) => <Tag color={ACTION_TAGS[v].color}>{ACTION_TAGS[v].label}</Tag>,
    },
    { title: '操作人', key: 'operator', width: 140, render: (_, r) => r.operatorName ?? r.operatorId ?? '-' },
    {
      title: '变更字段',
      key: 'diff',
      render: (_, r) => {
        const fields = diffSnapshot(r.beforeJson, r.afterJson);
        return fields.length ? fields.map((f) => <Tag key={f}>{f}</Tag>) : '-';
      },
    },
  ];

  return (
    <Drawer open={policyId !== null} width={960} onClose={onClose} title={`变更记录 · 策略 #${policyId ?? ''}`} destroyOnClose>
      <Spin spinning={loading}>
        {logs.length === 0 && !loading ? (
          <Empty description="暂无变更记录" />
        ) : (
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={logs}
            pagination={false}
            expandable={{
              expandedRowRender: (r) => (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="mb-1 font-medium">修改前</div>
                    <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap break-all bg-gray-50 p-2 text-xs">
                      {pretty(r.beforeJson)}
                    </pre>
                  </div>
                  <div>
                    <div className="mb-1 font-medium">修改后</div>
                    <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap break-all bg-gray-50 p-2 text-xs">
                      {pretty(r.afterJson)}
                    </pre>
                  </div>
                </div>
              ),
            }}
          />
        )}
      </Spin>
    </Drawer>
  );
};

export default LoadingPolicyLogDrawer;
